import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ShoppingCart, Package, Truck, Shield, Heart } from "lucide-react";
import { trackAddToCart, trackRemoveFromCart, trackMerchCheckout } from "@/utils/analytics";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// ─── Design tokens (AHP patriotic palette) ───────────────────────────────────
const T_LIGHT = {
  bg:           "#FAF9F6",
  cardBg:       "#fff",
  text:         "#1B1B1B",
  textMuted:    "rgba(27,27,27,0.52)",
  red:          "#B22234",
  navy:         "#002868",
  gold:         "#C5A55A",
  border:       "rgba(27,27,27,0.11)",
  borderNavy:   "rgba(0,40,104,0.18)",
  shadow:       "rgba(0,0,0,0.07)",
  hoverBg:      "rgba(178,34,52,0.04)",
  cartBtnBg:    "#fff",
  cartBtnBorder: "rgba(27,27,27,0.11)",
  cartBtnText:  "#1B1B1B",
  sectionBg:    "#002868",
  shippingBg:   "#FAF9F6",
  shippingLabel: "#002868",
  shippingText: "rgba(27,27,27,0.52)",
  shippingBorder: "rgba(27,27,27,0.11)",
};
const T_DARK = {
  bg:           "#0a1628",
  cardBg:       "#152240",
  text:         "#FAF9F6",
  textMuted:    "rgba(250,249,246,0.55)",
  red:          "#B22234",
  navy:         "#5B9BD5",
  gold:         "#C5A55A",
  border:       "rgba(250,249,246,0.10)",
  borderNavy:   "rgba(91,155,213,0.25)",
  shadow:       "rgba(0,0,0,0.3)",
  hoverBg:      "rgba(178,34,52,0.08)",
  cartBtnBg:    "#1e3358",
  cartBtnBorder: "rgba(250,249,246,0.12)",
  cartBtnText:  "#FAF9F6",
  sectionBg:    "#0d1f3c",
  shippingBg:   "#0a1628",
  shippingLabel: "#5B9BD5",
  shippingText: "rgba(250,249,246,0.55)",
  shippingBorder: "rgba(250,249,246,0.08)",
};

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans  = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ─── Product catalog ──────────────────────────────────────────────────────────
type BrandKey = 'ahp' | 'luigis' | 'antigreed' | 'both';
type Category = 'stickers' | 'apparel' | 'accessories';

interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  price: number;
  image: string;
  brand: BrandKey;
  category: Category;
  badge?: string;
  stripePriceId?: string;
}

const PRODUCTS: Product[] = [
  // ── AHP Products ──────────────────────────────────────────────────────────
  {
    id: 'ahp-bumper-sticker',
    name: 'Price Transparency Bumper Sticker',
    tagline: '"Know Your Price. Know Your Rights."',
    description: 'Premium 3"×10" vinyl bumper sticker. Weather-resistant, UV-stable. Start a conversation at every stoplight.',
    price: 4.99,
    image: '/merch/ahp-bumper-sticker.png',
    brand: 'ahp',
    category: 'stickers',
    badge: 'Best Seller',
  },
  {
    id: 'ahp-sticker-pack',
    name: 'AHP Activist Sticker Pack',
    tagline: '6 die-cut vinyl stickers',
    description: 'Six die-cut vinyl laptop stickers — AHP shield, slogans, and patriotic designs. Laptop, water bottle, anywhere you go.',
    price: 11.99,
    image: '/merch/ahp-sticker-sheet.png',
    brand: 'ahp',
    category: 'stickers',
  },
  {
    id: 'ahp-tshirt',
    name: 'American Hospital Prices T-Shirt',
    tagline: 'Classic unisex crew neck · S–3XL',
    description: 'Premium 100% cotton with bold AHP shield logo. Screen-printed in red, white, and blue. Available in white and navy.',
    price: 27.99,
    image: '/merch/ahp-tshirt.png',
    brand: 'ahp',
    category: 'apparel',
    badge: 'New',
  },
  {
    id: 'ahp-hat',
    name: 'AHP Shield Dad Hat',
    tagline: 'Navy · One size · Adjustable',
    description: 'Unstructured navy dad hat with gold-embroidered AHP shield. The kind of hat you wear to a town hall.',
    price: 29.99,
    image: '/merch/ahp-hat.png',
    brand: 'ahp',
    category: 'apparel',
  },
  {
    id: 'ahp-mug',
    name: '"My Deductible is HOW MUCH?!" Mug',
    tagline: '11oz ceramic · Dishwasher safe',
    description: 'White glossy ceramic mug with the AHP shield and the question on everyone\'s mind. A morning ritual with purpose.',
    price: 17.99,
    image: '/merch/ahp-mug.png',
    brand: 'ahp',
    category: 'accessories',
    badge: 'Fan Favorite',
  },
  {
    id: 'ahp-tote',
    name: 'Price Transparency Canvas Tote',
    tagline: '15"×16" · Heavy-duty canvas',
    description: 'Natural canvas tote with AHP branding screen-printed in navy and red ink. Roomy, reusable, and sends a message.',
    price: 22.99,
    image: '/merch/ahp-tote.png',
    brand: 'ahp',
    category: 'accessories',
  },

  // ── Luigi's Products ───────────────────────────────────────────────────────
  {
    id: 'lhos-bumper-sticker-pack',
    name: 'Awareness Bumper Sticker Pack (5)',
    tagline: '"CHECK YOUR PRICING"',
    description: '5 bumper stickers to spread healthcare transparency awareness. Luigi\'s Hall of Shame branding.',
    price: 12.99,
    image: '/merch/lhos-sticker.png',
    brand: 'luigis',
    category: 'stickers',
  },
  {
    id: 'lhos-window-decal',
    name: 'Car Window Decal',
    tagline: 'Premium vinyl · Easy apply',
    description: 'Premium vinyl window decal with Luigi\'s Hall of Shame logo and "Price Transparency Advocate".',
    price: 8.99,
    image: '/merch/lhos-decal.png',
    brand: 'luigis',
    category: 'stickers',
  },
  {
    id: 'lhos-tshirt',
    name: "Luigi's Hall of Shame T-Shirt",
    tagline: 'Comfortable cotton · Multiple sizes',
    description: 'Bold Hall of Shame logo. Available in multiple sizes. Wear it proudly.',
    price: 24.99,
    image: '/merch/lhos-tshirt.png',
    brand: 'luigis',
    category: 'apparel',
  },
  {
    id: 'lhos-laptop-sticker',
    name: 'Laptop Sticker Pack (10)',
    tagline: '10 die-cut vinyl stickers',
    description: '10 die-cut vinyl stickers with various healthcare transparency messages.',
    price: 15.99,
    image: '/merch/lhos-laptop-stickers.png',
    brand: 'luigis',
    category: 'stickers',
  },

  // ── AntiGreed Products ────────────────────────────────────────────────────
  {
    id: 'ag-bumper-sticker-pack',
    name: 'Late Stage Capitalism Bumper Stickers (5)',
    tagline: '"Know What You\'re Paying"',
    description: '5 bumper stickers exposing price gouging. AntiGreed.app branding.',
    price: 12.99,
    image: '/merch/ag-sticker.png',
    brand: 'antigreed',
    category: 'stickers',
  },
  {
    id: 'ag-tshirt',
    name: 'AntiGreed.app T-Shirt',
    tagline: 'Show your resistance',
    description: 'Comfortable cotton tee with AntiGreed logo. Fight back against price gouging.',
    price: 24.99,
    image: '/merch/ag-tshirt.png',
    brand: 'antigreed',
    category: 'apparel',
  },
  {
    id: 'ag-laptop-sticker',
    name: 'Capitalism Reality Check Stickers (10)',
    tagline: '10 die-cut vinyl stickers',
    description: '10 die-cut vinyl stickers with satirical late stage capitalism messages.',
    price: 15.99,
    image: '/merch/ag-laptop-stickers.png',
    brand: 'antigreed',
    category: 'stickers',
  },
];

// ─── AHP Store (patriotic inline-styled design) ───────────────────────────────
const AHPStore = ({ products, loading = false }: { products: Product[]; loading?: boolean }) => {
  const { toast } = useToast();
  const { isDark } = useTheme();
  const T = isDark ? T_DARK : T_LIGHT;
  const [cart, setCart] = useState<Record<string, number>>({});
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  const addToCart = (id: string) => {
    setCart(prev => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
    toast({ title: "Added to cart!", description: "Item added. Checkout coming soon." });
    const product = products.find(x => x.id === id);
    if (product) trackAddToCart(product.name, product.price, 1);
  };

  const removeFromCart = (id: string) => {
    setCart(prev => {
      const next = { ...prev };
      if (next[id] > 1) next[id]--;
      else delete next[id];
      return next;
    });
    const product = products.find(x => x.id === id);
    if (product) trackRemoveFromCart(product.name);
  };

  const cartTotal  = Object.entries(cart).reduce((sum, [id, qty]) => {
    const p = products.find(x => x.id === id);
    return sum + (p?.price ?? 0) * qty;
  }, 0);
  const cartCount  = Object.values(cart).reduce((a, b) => a + b, 0);

  const handleCheckout = () => {
    toast({ title: "Checkout Coming Soon!", description: "Printify + Stripe integration in progress. Check back soon!" });
    trackMerchCheckout(cartTotal, cartCount);
  };

  const stickers    = products.filter(p => p.category === 'stickers');
  const apparel     = products.filter(p => p.category === 'apparel');
  const accessories = products.filter(p => p.category === 'accessories');

  const CATEGORY_BADGE: Record<Category, { label: string; bg: string; color: string }> = {
    stickers:    { label: 'Stickers & Decals', bg: 'rgba(178,34,52,0.10)', color: T.red },
    apparel:     { label: 'Apparel',           bg: 'rgba(0,40,104,0.10)', color: T.navy },
    accessories: { label: 'Accessories',       bg: 'rgba(197,165,90,0.18)', color: '#8B7032' },
  };

  const ProductCard = ({ product }: { product: Product }) => {
    const cat = CATEGORY_BADGE[product.category];
    const inCart = cart[product.id] ?? 0;
    const imgFailed = imgErrors[product.id];

    return (
      <div style={{
        background: T.cardBg,
        border: `1px solid ${T.borderNavy}`,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: `0 2px 12px ${T.shadow}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s',
      }}>
        {/* Product image */}
        <div style={{
          height: 220,
          background: `linear-gradient(135deg, rgba(0,40,104,0.05) 0%, rgba(178,34,52,0.04) 100%)`,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {!imgFailed ? (
            <img
              src={product.image}
              alt={product.name}
              onError={() => setImgErrors(prev => ({ ...prev, [product.id]: true }))}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🇺🇸</div>
              <div style={{ fontFamily: sans, fontSize: 12, color: T.textMuted }}>Image generating...</div>
            </div>
          )}
          {/* Badge */}
          {product.badge && (
            <div style={{
              position: 'absolute', top: 12, right: 12,
              background: T.red, color: '#fff',
              fontFamily: sans, fontSize: 11, fontWeight: 600,
              padding: '3px 10px', borderRadius: 20, letterSpacing: '0.05em',
            }}>
              {product.badge}
            </div>
          )}
          {/* Category pill */}
          <div style={{
            position: 'absolute', bottom: 10, left: 12,
            background: cat.bg, color: cat.color,
            fontFamily: sans, fontSize: 11, fontWeight: 600,
            padding: '3px 10px', borderRadius: 20, letterSpacing: '0.05em',
            backdropFilter: 'blur(4px)',
          }}>
            {cat.label}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '18px 20px 20px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontFamily: serif, fontSize: 18, fontWeight: 400, color: T.text, lineHeight: 1.3 }}>
            {product.name}
          </div>
          <div style={{ fontFamily: sans, fontSize: 12, color: T.red, letterSpacing: '0.02em' }}>
            {product.tagline}
          </div>
          <div style={{ fontFamily: sans, fontSize: 13.5, color: T.textMuted, lineHeight: 1.5, flex: 1, marginTop: 4 }}>
            {product.description}
          </div>

          {/* Price + Cart controls */}
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontFamily: serif, fontSize: 26, color: T.red, fontWeight: 400 }}>
              ${product.price.toFixed(2)}
            </div>
            {inCart === 0 ? (
              <button onClick={() => addToCart(product.id)} style={{
                background: isDark ? '#5B9BD5' : '#002868', color: '#FAF9F6',
                border: 'none', borderRadius: 8, padding: '10px 18px',
                fontFamily: sans, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                transition: 'background 0.15s',
              }}>
                <ShoppingCart size={15} />
                Add to Cart
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => removeFromCart(product.id)} style={{
                  width: 32, height: 32, borderRadius: 6,
                  border: `1px solid ${T.border}`, background: T.cartBtnBg,
                  fontFamily: sans, fontSize: 16, cursor: 'pointer', color: T.cartBtnText,
                }}>−</button>
                <span style={{ fontFamily: sans, fontSize: 15, fontWeight: 600, color: T.navy, minWidth: 20, textAlign: 'center' }}>
                  {inCart}
                </span>
                <button onClick={() => addToCart(product.id)} style={{
                  width: 32, height: 32, borderRadius: 6,
                  border: `1px solid ${isDark ? '#5B9BD5' : '#002868'}`, background: isDark ? '#5B9BD5' : '#002868',
                  fontFamily: sans, fontSize: 16, cursor: 'pointer', color: '#fff',
                }}>+</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const SectionHeader = ({ label, emoji }: { label: string; emoji: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '40px 0 20px' }}>
      <div style={{ fontFamily: serif, fontSize: 28, color: T.text }}>{emoji} {label}</div>
      <div style={{ flex: 1, height: 1, background: T.border }} />
    </div>
  );

  return (
    <div style={{ background: T.bg, minHeight: '100vh', fontFamily: sans }}>

      {/* Top red stripe */}
      <div style={{ background: T.red, height: 4 }} />

      {/* Hero header */}
      <div style={{ background: T.bg, padding: '52px 24px 40px', textAlign: 'center', borderBottom: `1px solid ${T.border}` }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ fontFamily: sans, fontSize: 12, letterSpacing: '0.2em', color: T.red, marginBottom: 18, fontWeight: 600 }}>
            ★ &nbsp; ★ &nbsp; ★ &nbsp; &nbsp; AMERICAN HOSPITAL PRICES STORE &nbsp; &nbsp; ★ &nbsp; ★ &nbsp; ★
          </div>
          <h1 style={{ fontFamily: serif, fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 400, color: T.text, margin: '0 0 14px', lineHeight: 1.15 }}>
            Wear the movement.<br />Spread the word.
          </h1>
          <p style={{ fontFamily: sans, fontSize: 17, color: T.textMuted, margin: '0 0 28px', lineHeight: 1.6 }}>
            Every purchase funds healthcare price transparency.<br />
            100% of profits go toward maintaining this platform — for free, for everyone.
          </p>
          {cartCount > 0 && (
            <button onClick={handleCheckout} style={{
              background: T.red, color: '#FAF9F6',
              border: 'none', borderRadius: 10, padding: '14px 32px',
              fontFamily: sans, fontSize: 15, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 10,
              boxShadow: `0 4px 16px rgba(178,34,52,0.30)`,
            }}>
              <ShoppingCart size={18} />
              Checkout — ${cartTotal.toFixed(2)}
              <span style={{
                background: '#fff', color: T.red,
                borderRadius: 20, padding: '1px 8px', fontSize: 12, fontWeight: 700,
              }}>{cartCount}</span>
            </button>
          )}
        </div>
      </div>

      {/* Info strip */}
      <div style={{ background: T.sectionBg }}>
        <div style={{
          maxWidth: 960, margin: '0 auto', padding: '18px 24px',
          display: 'flex', justifyContent: 'center', gap: 'clamp(24px, 6vw, 72px)',
          flexWrap: 'wrap',
        }}>
          {[
            { icon: <Truck size={16} />, title: 'Free Shipping', sub: 'Orders over $30' },
            { icon: <Heart size={16} />, title: 'Funds the Mission', sub: '100% of profit' },
            { icon: <Shield size={16} />, title: 'Secure Checkout', sub: 'Stripe · Printify' },
          ].map(({ icon, title, sub }) => (
            <div key={title} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ color: T.gold, opacity: 0.9 }}>{icon}</div>
              <div>
                <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: '#FAF9F6' }}>{title}</div>
                <div style={{ fontFamily: sans, fontSize: 12, color: 'rgba(250,249,246,0.60)' }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Products */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '8px 24px 64px' }}>

        {loading && (
          <div style={{ textAlign: 'center', padding: '80px 0', color: T.textMuted, fontFamily: sans }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🇺🇸</div>
            <div style={{ fontSize: 14 }}>Loading store...</div>
          </div>
        )}

        {!loading && stickers.length > 0 && (
          <>
            <SectionHeader label="Stickers & Decals" emoji="🗽" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
              {stickers.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </>
        )}

        {!loading && apparel.length > 0 && (
          <>
            <SectionHeader label="Apparel" emoji="👕" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
              {apparel.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </>
        )}

        {!loading && accessories.length > 0 && (
          <>
            <SectionHeader label="Home & Accessories" emoji="☕" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
              {accessories.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </>
        )}
      </div>

      {/* Mission section */}
      <div style={{ background: T.sectionBg, padding: '56px 24px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontFamily: sans, fontSize: 12, letterSpacing: '0.2em', color: T.gold, marginBottom: 16, fontWeight: 600 }}>
            ★ &nbsp; ★ &nbsp; ★
          </div>
          <h2 style={{ fontFamily: serif, fontSize: 34, fontWeight: 400, color: '#FAF9F6', margin: '0 0 20px', lineHeight: 1.3 }}>
            Why Buy Merch?
          </h2>
          <p style={{ fontFamily: sans, fontSize: 16, color: 'rgba(250,249,246,0.72)', lineHeight: 1.7, margin: '0 0 28px' }}>
            We built americanhospitalprices.com to be free for every American —
            no paywalls, no subscriptions. Merch is how we keep the lights on
            and the database growing. Every bumper sticker is a tiny billboard
            for price transparency. Every shirt is a conversation starter.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, textAlign: 'left' }}>
            {[
              { icon: '📢', text: 'Spread awareness to millions who don\'t know they can compare prices' },
              { icon: '💰', text: '100% of profits fund server costs, data, and development' },
              { icon: '🇺🇸', text: 'Join a movement pushing for real healthcare transparency nationwide' },
              { icon: '✅', text: 'Print-on-demand via Printify — no waste, ships direct to you' },
            ].map(({ icon, text }) => (
              <div key={icon} style={{
                background: 'rgba(250,249,246,0.07)',
                borderRadius: 10, padding: '16px 18px',
                display: 'flex', gap: 12, alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: 20 }}>{icon}</span>
                <span style={{ fontFamily: sans, fontSize: 13.5, color: 'rgba(250,249,246,0.78)', lineHeight: 1.5 }}>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Shipping & fulfillment info */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '52px 24px 64px' }}>
        <h2 style={{ fontFamily: serif, fontSize: 28, fontWeight: 400, color: T.text, margin: '0 0 20px' }}>
          Shipping & Returns
        </h2>
        <div style={{ display: 'grid', gap: 14 }}>
          {[
            { label: 'Fulfillment', text: 'Orders are printed and shipped via Printify within 3–5 business days.' },
            { label: 'Shipping', text: 'US domestic only for now. Standard shipping 4–7 days. Free on orders over $30.' },
            { label: 'Returns', text: 'Not happy? Email us within 30 days and we\'ll make it right — full refund or replacement.' },
            { label: 'International', text: 'International shipping coming soon as demand grows.' },
            { label: 'Questions', text: 'hello@americanhospitalprices.com — we actually reply.' },
          ].map(({ label, text }) => (
            <div key={label} style={{
              display: 'flex', gap: 16,
              padding: '14px 0',
              borderBottom: `1px solid ${T.shippingBorder}`,
            }}>
              <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, color: T.shippingLabel, minWidth: 110 }}>{label}</div>
              <div style={{ fontFamily: sans, fontSize: 14, color: T.shippingText, lineHeight: 1.55 }}>{text}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Gold accent line + bottom navy stripe */}
      <div style={{ height: 2, background: T.gold }} />
      <div style={{ background: T.sectionBg, height: 6 }} />
    </div>
  );
};

// ─── Legacy store (Luigi's / AntiGreed) ───────────────────────────────────────
const LegacyStore = ({ products }: { products: Product[] }) => {
  const brand = useBrand();
  const { toast } = useToast();
  const [cart, setCart] = useState<Record<string, number>>({});

  const isAntiGreed = brand.key === 'antigreed';

  const addToCart = (id: string) => {
    setCart(prev => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
    toast({ title: "Added to cart!", description: "Item has been added to your cart." });
  };

  const cartTotal = Object.entries(cart).reduce((total, [id, qty]) => {
    const p = products.find(x => x.id === id);
    return total + (p?.price ?? 0) * qty;
  }, 0);
  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);

  const handleCheckout = async () => {
    toast({ title: "Checkout Coming Soon!", description: "Stripe integration is being finalized. Check back soon!" });
  };

  return (
    <div className={`min-h-screen pb-24 ${isAntiGreed ? 'bg-gradient-to-br from-amber-50 to-orange-50' : 'bg-gradient-to-br from-background via-background/95 to-primary/5'}`}>
      <div className={isAntiGreed
        ? 'bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border-b-2 border-amber-200 shadow-lg'
        : 'bg-gradient-to-br from-matrix-green/10 to-blood-red/10 border-b border-matrix-green/20'
      }>
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-4xl font-bold mb-3 ${isAntiGreed ? 'text-emerald-800 font-serif' : 'text-foreground'}`}>
                <ShoppingCart className="inline-block w-10 h-10 mr-3 mb-2" />
                Merch Store
              </h1>
              <p className={`text-lg ${isAntiGreed ? 'text-gray-700' : 'text-muted-foreground'}`}>
                Spread awareness. Support the mission. Look good doing it.
              </p>
            </div>
            {cartCount > 0 && (
              <Button size="lg" onClick={handleCheckout} className={isAntiGreed ? 'bg-emerald-700 hover:bg-emerald-800' : ''}>
                <ShoppingCart className="mr-2" />
                Checkout (${cartTotal.toFixed(2)})
                <Badge className="ml-2 bg-white text-black">{cartCount}</Badge>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <Card key={product.id} className={`overflow-hidden hover:shadow-lg transition-shadow ${isAntiGreed ? 'bg-white/90 border-amber-200' : 'border-matrix-green/20'}`}>
              <div className={`h-48 flex items-center justify-center relative ${isAntiGreed ? 'bg-gradient-to-br from-amber-100 to-orange-100' : 'bg-gradient-to-br from-matrix-green/10 to-primary/5'}`}>
                <Package className={`w-24 h-24 ${isAntiGreed ? 'text-emerald-600' : 'text-matrix-green'} opacity-30`} />
                <div className="absolute text-center px-4">
                  <p className="font-mono text-sm text-muted-foreground">Product image</p>
                  <p className="font-mono text-xs text-muted-foreground">coming soon</p>
                </div>
              </div>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className={`text-lg ${isAntiGreed ? 'text-gray-900' : ''}`}>{product.name}</CardTitle>
                  <Badge className={product.category === 'stickers' ? 'bg-blue-100 text-blue-800' : product.category === 'apparel' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}>
                    {product.category}
                  </Badge>
                </div>
                <CardDescription className={isAntiGreed ? 'text-gray-600' : ''}>{product.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-matrix-green">${product.price.toFixed(2)}</div>
                  <Button onClick={() => addToCart(product.id)} className={isAntiGreed ? 'bg-emerald-700 hover:bg-emerald-800' : ''}>
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Add to Cart
                    {cart[product.id] > 0 && <Badge className="ml-2 bg-white text-black">{cart[product.id]}</Badge>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Category slug → display category mapping ─────────────────────────────────
const SLUG_TO_CATEGORY: Record<string, Category> = {
  stickers:    'stickers',
  apparel:     'apparel',
  headwear:    'apparel',
  drinkware:   'accessories',
  bags:        'accessories',
  prints:      'accessories',
  home:        'accessories',
  accessories: 'accessories',
  novelty:     'accessories',
};

// ─── Main export ──────────────────────────────────────────────────────────────
const Merch = () => {
  const brand = useBrand();

  // AHP: load approved products live from DB so admin approvals show immediately
  const { data: dbRows, isLoading: ahpLoading } = useQuery({
    queryKey: ['merch_products', 'ahp', 'approved'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('merch_products')
        .select('id, name, tagline, description, price, image_path, badge, category_slug, sort_order')
        .eq('brand', 'ahp')
        .eq('status', 'approved')
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: brand.key === 'ahp',
    staleTime: 60_000,
  });

  if (brand.key === 'ahp') {
    const ahpProducts: Product[] = (dbRows ?? []).map(row => ({
      id:          row.id,
      name:        row.name,
      tagline:     row.tagline  ?? '',
      description: row.description ?? '',
      price:       parseFloat(row.price) ?? 0,
      image:       row.image_path ?? '',
      brand:       'ahp' as BrandKey,
      category:    (SLUG_TO_CATEGORY[row.category_slug] ?? 'accessories') as Category,
      badge:       row.badge ?? undefined,
    }));
    // Show skeleton while loading; fall back to hardcoded if DB has nothing yet
    const displayProducts = ahpProducts.length > 0
      ? ahpProducts
      : PRODUCTS.filter(p => p.brand === 'ahp');
    return <AHPStore products={displayProducts} loading={ahpLoading && ahpProducts.length === 0} />;
  }

  const filteredProducts = PRODUCTS.filter(
    p => p.brand === brand.key || p.brand === 'both'
  );
  return <LegacyStore products={filteredProducts} />;
};

export default Merch;
